import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  Divider,
  IconButton,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  TextField, // 🆕 ADD THIS
} from "@mui/material";
import {
  Close,
  Groups,
  PersonAdd,
  CheckCircle,
  Comment, // 🆕 ADD THIS
} from "@mui/icons-material";
import { url } from "../constants/url";

const TeamCollaborationModal = ({ 
  open, 
  onClose, 
  lead, 
  userData,
  onSuccess 
}) => {
  const theme = useTheme();
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ open: false, severity: "success", message: "" });
  
  // 🆕 NEW STATE FOR COLLABORATION COMMENT
  const [collaborationComment, setCollaborationComment] = useState("");

  // Status display mapping
  const statusDisplayMap = {
    hot: "Hot",
    warm: "Warm", 
    closed: "Closed",
    dead: "Dead",
    rescheduled: "Rescheduled",
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    meeting_scheduled: "Meeting Scheduled",
    call_not_received: "Not Received",
    "follow-up": "Follow Up",
    send_quotation: "Send Quotation",
    received: "Received",
    switch_off: "Switch Off",
    wrong_number: "Wrong Number",
    token_received: "Token Received",
    partially_paid: "Partially Paid",
    fully_paid: 'Fully Paid',
    quotation_rejected:"Quotation Rejected",
    quotation_expired:"Quotation Expired",
    negotiation:"Negotiation",
    approved:"Approved",
    rejected:"Rejected",
    pending:"Pending",
    sent:"Sent",
    overdue: "Overdue Payment",
    payment_overdue: "Payment Overdue",
    quotation_revised:"Quotation Revised",
  };

  const getCompanyId = () => {
    if (userData?.companyAdded && userData.companyAdded.length > 0) {
      const approvedCompany = userData.companyAdded.find(company => 
        company.status === 'approved'
      );
      if (approvedCompany && approvedCompany.companyId) {
        return approvedCompany.companyId;
      }
    }
    
    if (userData?.company && userData.company.length > 0) {
      const company = userData.company[0];
      if (company.companyId) {
        return company.companyId;
      }
    }
    
    return null;
  };

  const UID = userData?._id;
  const CID = getCompanyId();
  const userRole = userData?.role;

  useEffect(() => {
    if (open && lead && CID) {
      fetchTeamMembers();
    } else if (open && lead && !CID) {
      showAlert("error", "Cannot find company information. Please contact administrator.");
    }
  }, [open, lead, CID]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const apiUrl = `${url}/api/company/company-members-universal?companyId=${CID}&userId=${UID}`;

      const response = await fetch(apiUrl);
      const data = await response.json();

      if (data.success && data.data) {
        const { members } = data.data;
        
        const filteredMembers = members.filter(user => {
          if (user._id === UID) return false;
          
          const userRoleLower = (user.role || '').toLowerCase().trim();
          const currentUserRoleLower = (userRole || '').toLowerCase().trim();
          
          if (currentUserRoleLower === 'admin') {
            const allowedRoles = ['teammember', 'team member'];
            return allowedRoles.includes(userRoleLower);
          }
          
          if (currentUserRoleLower === 'teammember' || currentUserRoleLower === 'team member') {
            const allowedRoles = ['admin', 'teammember', 'team member'];
            return allowedRoles.includes(userRoleLower);
          }
          
          return false;
        });

        setTeamMembers(filteredMembers);
        
      } else {
        showAlert("error", data.message || "Failed to fetch company members");
        setTeamMembers([]);
      }
    } catch (error) {
      showAlert("error", "Failed to fetch team members");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (severity, message) => {
    setAlert({ open: true, severity, message });
    setTimeout(() => setAlert({ ...alert, open: false }), 6000);
  };

  const handleMemberSelect = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleCheckboxClick = (event, memberId) => {
    event.stopPropagation();
    handleMemberSelect(memberId);
  };

  const handleListItemClick = (memberId) => {
    handleMemberSelect(memberId);
  };

  const handleCreateCollaboration = async () => {
    if (selectedMembers.length === 0) {
      showAlert("error", "Please select at least one team member");
      return;
    }

    if (!CID) {
      showAlert("error", "Company information not found. Cannot create collaboration.");
      return;
    }

    // 🆕 VALIDATE COMMENT
    if (!collaborationComment.trim()) {
      showAlert("error", "Please provide a reason for collaboration");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        leadId: lead._id,
        participantIds: selectedMembers,
        userId: UID,
        companyId: CID,
        collaborationComment: collaborationComment.trim() // 🆕 ADD COMMENT TO PAYLOAD
      };

      const response = await fetch(`${url}/api/team-collaboration/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        showAlert("success", "Team collaboration created successfully!");
        onSuccess();
        onClose();
        setSelectedMembers([]);
        setCollaborationComment(""); // 🆕 RESET COMMENT
      } else {
        throw new Error(data.message || "Failed to create team collaboration");
      }
    } catch (error) {
      showAlert("error", error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedMembers([]);
    setCollaborationComment(""); // 🆕 RESET COMMENT
    setAlert({ ...alert, open: false });
    onClose();
  };

  const getRoleColor = (role) => {
    const roleLower = (role || '').toLowerCase();
    switch (roleLower) {
      case "admin": return "#dc2626";
      case "teammember": 
      case "team member": return "#2563eb";
      default: return "#6b7280";
    }
  };

  const getRoleText = (role) => {
    const roleLower = (role || '').toLowerCase();
    switch (roleLower) {
      case "admin": return "Admin";
      case "teammember": 
      case "team member": return "Team Member";
      default: return role;
    }
  };

  const getDisplayStatus = (status) => {
    return statusDisplayMap[status] || status?.charAt(0)?.toUpperCase() + status?.slice(1) || "N/A";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: 'white',
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          mb:2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: '#f3f4f6',
              color: '#374151',
            }}
          >
            <Groups />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="600" color="#111827">
              Team Collaboration
            </Typography>
            <Typography variant="body2" color="#6b7280">
              Add team members to collaborate
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Alert */}
      {alert.open && (
        <Alert 
          severity={alert.severity} 
          sx={{ mx: 3, mt: 2 }}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          {alert.message}
        </Alert>
      )}

      <DialogContent sx={{ py: 2, backgroundColor: 'white' }}>
        {/* Lead Info Card */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" gutterBottom color="#111827">
            Lead Details
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="caption" color="#6b7280">
                Client Name
              </Typography>
              <Typography variant="body2" fontWeight="500" color="#111827">
                {lead?.clientName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="#6b7280">
                Contact
              </Typography>
              <Typography variant="body2" fontWeight="500" color="#111827">
                {lead?.contactNo}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="#6b7280">
                Status
              </Typography>
              <Chip
                label={getDisplayStatus(lead?.status)}
                size="small"
                sx={{
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  fontWeight: '500',
                  ml: 1
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* 🆕 COLLABORATION COMMENT SECTION */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
          }}
        >
          <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#0369a1">
            <Comment sx={{ fontSize: 18, mr: 1, verticalAlign: "middle" }} />
            Collaboration Purpose
          </Typography>
          <Typography variant="body2" color="#0c4a6e" sx={{ mb: 2 }}>
            Please provide a reason for collaborating on this lead. This comment will be visible to all participants.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Explain why you're adding team members to collaborate on this lead. For example: 'Need technical expertise for service discussion', 'Require sales support for negotiation', etc."
            value={collaborationComment}
            onChange={(e) => setCollaborationComment(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 1,
              }
            }}
          />
        </Box>

        {/* Team Members Selection */}
        <Typography variant="subtitle1" fontWeight="600" gutterBottom color="#111827">
          Select Team Members
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : teamMembers.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <PersonAdd sx={{ fontSize: 48, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2" color="#6b7280">
              No team members available
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: "auto", backgroundColor: 'white' }}>
            {teamMembers.map((member, index) => (
              <React.Fragment key={member._id}>
                <ListItem
                  sx={{
                    px: 0,
                    py: 1.5,
                    borderRadius: 1,
                    cursor: "pointer",
                    backgroundColor: 'white',
                    "&:hover": {
                      backgroundColor: '#f9fafb',
                    },
                  }}
                  onClick={() => handleListItemClick(member._id)}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: alpha(getRoleColor(member.role), 0.1),
                        color: getRoleColor(member.role),
                        fontWeight: "600",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {member.name?.charAt(0)?.toUpperCase() || "U"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight="500" color="#111827">
                          {member.name || "Unknown User"}
                        </Typography>
                        <Chip
                          label={getRoleText(member.role)}
                          size="small"
                          sx={{
                            color: getRoleColor(member.role),
                            backgroundColor: alpha(getRoleColor(member.role), 0.1),
                            border: `1px solid ${getRoleColor(member.role)}`,
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: '500'
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="#6b7280">
                        {member.email || "No email"}
                      </Typography>
                    }
                  />
                  <Checkbox
                    checked={selectedMembers.includes(member._id)}
                    onChange={(event) => handleCheckboxClick(event, member._id)}
                    onClick={(event) => event.stopPropagation()}
                    size="small"
                    sx={{
                      color: '#d1d5db',
                      '&.Mui-checked': {
                        color: '#10b981',
                      },
                    }}
                  />
                </ListItem>
                {index < teamMembers.length - 1 && (
                  <Divider component="li" sx={{ borderColor: '#e5e7eb' }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Selected Members Summary */}
        {selectedMembers.length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
            }}
          >
            <Typography variant="body2" fontWeight="600" color="#166534">
              <CheckCircle sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }} />
              {selectedMembers.length} member{selectedMembers.length > 1 ? "s" : ""} selected for collaboration
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2, backgroundColor: 'white', borderTop: '1px solid #e5e7eb' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2, 
            px: 3,
            borderColor: '#d1d5db',
            color: '#374151',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateCollaboration}
          variant="contained"
          disabled={selectedMembers.length === 0 || submitting || !CID || !collaborationComment.trim()}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Groups />}
          sx={{ 
            borderRadius: 2, 
            px: 4,
            backgroundColor: "#276221",
            color: 'white',
            fontWeight: '600',
            "&:hover": {
              backgroundColor: "#276221",
            },
            "&.Mui-disabled": {
              backgroundColor: "#d1d5db",
              color: "#9ca3af"
            }
          }}
        >
          {submitting ? "Creating..." : `Create Collaboration`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamCollaborationModal;